import { useState } from "react";
import PropTypes from "prop-types";
import Pagination from "./pagination";

const PlanBadge = ({ plan }) => {
  const colors = {
    Free: "bg-green-100 text-green-500",
    Bronze: "bg-orange-100 text-orange-500",
    VIP: "bg-yellow-100 text-yellow-500",
  };
  return <span className={`px-2 py-1 text-xs font-semibold rounded ${colors[plan]}`}>{plan}</span>;
};

PlanBadge.propTypes = {
  plan: PropTypes.string.isRequired,
};

const PlanStatsCard = ({ plan, count }) => {
  const colors = {
    Free: "bg-green-200 text-green-800",
    Bronze: "bg-[url('/src/assets/plans/bronze.svg')] bg-cover bg-center text-orange-800",  
    VIP: "bg-[url('/src/assets/plans/vip.svg')] bg-cover bg-center text-white font-bold",  
  };
  return (
    <div className={`flex flex-col items-center p-4 rounded-lg shadow-md ${colors[plan]}`}>
      <h3 className="font-semibold text-lg">{plan} </h3>
      <p className="text-3xl font-bold">{count}</p>
      <p className="text-sm text-gray-100">Users</p>
    </div>
  );
};

PlanStatsCard.propTypes = {
  plan: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
};

const UserTable = ({ users }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState(""); // Added search term state
  const usersPerPage = 10;

  // Filter users based on search term
  const filteredUsers = users.filter((user) =>
    [
      user.name?.toLowerCase(),
      user.email?.toLowerCase(),
      user.phone?.toLowerCase(),
    ].some((field) => field?.includes(searchTerm.toLowerCase()))
  );

  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Handle search input change
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Pagination Logic
  const startIndex = (currentPage - 1) * usersPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + usersPerPage);

  // Calculate users per plan (using filtered users)
  const planCounts = filteredUsers.reduce((counts, user) => {
    counts[user.plan] = (counts[user.plan] || 0) + 1;
    return counts;
  }, {});

  const closeModal = () => {
    setShowModal(false);
    setSelectedUser(null);
  };

  return (
    <div className="bg-white shadow rounded-xl p-8 w-full">
      {/* Plan Stats Section */}
      <div className="mb-6 grid grid-cols-3 gap-6 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {Object.keys(planCounts).map((plan) => (
          <PlanStatsCard key={plan} plan={plan} count={planCounts[plan]} />
        ))}
      </div>

      <input
        type="text"
        placeholder="Search for ID, Email, Name"
        className="w-full sm:w-full mb-4 p-2 border border-gray-200 rounded-lg focus:outline-none focus:ring"
        value={searchTerm}
        onChange={handleSearch}
      />

      {/* Cards layout on mobile */}
      <div className="w-full sm:block md:hidden">
        {paginatedUsers.map((user, index) => (
          <div key={index} className="bg-white shadow rounded-lg mb-4 p-4">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold">{user.name}</h3>
              <PlanBadge plan={user.plan} />
            </div>
            <p className="text-sm text-gray-500">{user.email}</p>
            <p className="text-sm text-gray-500">{user.phone}</p>
            <p className="text-sm text-gray-500">Payment: {user.payment}</p>
          </div>
        ))}
      </div>

      {/* Table layout on larger screens */}
      <div className="w-full hidden md:block">
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse whitespace-nowrap table-auto">
            <thead>
              <tr className="bg-gray-100 text-left text-gray-600 text-sm">
                <th className="p-2"><input type="checkbox" /></th>
                <th className="p-2">Name</th>
                <th className="p-2">Contact</th>
                <th className="p-2">Current Plan</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.map((user, index) => (
                <tr key={index} className="border-t text-sm text-gray-700">
                  <td className="p-2"><input type="checkbox" /></td>
                  <td className="p-2">{user.name}</td>
                  <td className="p-2">
                    <div>{user.email}</div>
                    <div className="text-gray-500 text-xs">{user.phone}</div>
                  </td>
                  <td className="p-2"><PlanBadge plan={user.plan} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Component */}
      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />

      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-md bg-opacity-50 flex justify-center items-center">
          <div className="bg-white p-6 rounded-2xl shadow-lg w-96">
            <h2 className="text-lg font-semibold mb-4">Confirm Action</h2>
            <p>Are you sure you want to lock {selectedUser?.name}&apos;s account?</p>
            <div className="flex justify-end space-x-2 mt-12">
              <button className="px-4 py-2 bg-gray-300 rounded" onClick={closeModal}>Cancel</button>
              <button className="px-4 py-2 bg-secondary text-white rounded">Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

UserTable.propTypes = {
  users: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
      phone: PropTypes.string.isRequired,
      plan: PropTypes.string.isRequired,
      payment: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default UserTable;